import { Button, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';

const CustomAgNumberFilter = (props: any) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [value, setValue] = useState('');
  const [isActive, setIsActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const isTypingRef = useRef(false);

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (!/^[-0-9.,]*$/.test(newValue)) return;

      setValue(newValue);
      // reflect active state immediately while typing
      setIsActive(!!newValue);

      isTypingRef.current = true;

      props.parentFilterInstance?.((instance: any) => {
        instance.onFloatingFilterChanged('contains', newValue);
      });

      // Allow small delay before resetting typing state
      setTimeout(() => {
        isTypingRef.current = false;
      }, 300);
    },
    [props],
  );

  // Reset input if filter cleared externally
  useEffect(() => {
    if (!props.model) setValue('');
  }, [props.model]);

  useLayoutEffect(() => {
    const updateFromModel = () => {
      if (isTypingRef.current || document.activeElement === inputRef.current) return;

      const filterModel = props.api.getFilterModel?.();
      const colId = props.column.getColId?.();
      const model = filterModel?.[colId];

      // Active if simple model.filter exists (string or number) or any condition has a filter
      const hasSimple =
        model?.filter !== undefined && model?.filter !== null && String(model?.filter).length > 0;
      const hasFromConditions =
        Array.isArray(model?.conditions) &&
        model.conditions.some(
          (c: any) => c?.filter !== undefined && c?.filter !== null && String(c.filter).length > 0,
        );
      const modelHasValue = !!(hasSimple || hasFromConditions);

      // Prefer first condition value when present
      const firstConditionValue =
        Array.isArray(model?.conditions) && model.conditions.length > 0
          ? String(model.conditions[0]?.filter ?? '')
          : String(model?.filter ?? '');

      if (modelHasValue) {
        setValue(firstConditionValue);
        setIsActive(true);
      } else {
        setValue('');
        setIsActive(false);
      }
    };

    updateFromModel();
    props.api?.addEventListener('filterChanged', updateFromModel);

    return () => {
      props.api?.removeEventListener('filterChanged', updateFromModel);
    };
  }, [props.api, props.column]);

  // Open AG Grid filter popup below the input
  const onFilterIconClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    props.showParentFilter?.(inputRef.current);
  };

  return (
    <Box
      sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <Stack
        direction="row"
        sx={{
          border: `1px solid ${theme.palette.borderLight}`,
          borderRadius: `${theme.borderRadius.sm}px`,
          backgroundColor: theme.palette.common.white,
          width: '100%',
          height: '25px',
          gap: '2px',
          overflow: 'hidden',
        }}
        alignItems={'center'}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <TextField
          value={value}
          onChange={onInputChange}
          placeholder={t('common.search')}
          inputRef={inputRef}
          inputProps={{ inputMode: 'numeric', pattern: '[-0-9.,]*' }}
          onKeyDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            flex: 1,
            minWidth: 0,
            '& .MuiOutlinedInput-root': {
              padding: 0,
              boxShadow: 'none',
              paddingLeft: '5px',
              fontSize: '12px',
              '& fieldset': { border: 'none' },
              '&:hover fieldset': { border: 'none' },
              '&.Mui-focused fieldset': { border: 'none' },
            },
            '& .MuiOutlinedInput-root.Mui-focused': {
              boxShadow: 'none',
            },
            '& .MuiOutlinedInput-input': {
              padding: 0,
              height: '20px',
            },
          }}
        />

        {/* Filter icon */}
        <Button
          type="button"
          className="ag-icon ag-icon-filter"
          sx={{
            cursor: 'pointer',
            fontSize: '14px',
            height: '100%',
            width: '20px',
            minWidth: '30px',
            padding: 0,
            borderRadius: '0px',
            backgroundColor: isActive ? theme.palette.leftNavigation.activeBg : 'transparent',
            color: isActive ? theme.palette.primary.main : theme.palette.textMedium,
            transition: `all ${theme.transitions.custom.fast}`,
            '&:hover': {
              backgroundColor: isActive
                ? theme.palette.leftNavigation.activeBg
                : 'rgba(0,0,0,0.05)',
            },
          }}
          onClick={onFilterIconClick}
        />
      </Stack>
    </Box>
  );
};

export default CustomAgNumberFilter;
